import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { getEnvVarsQPR } from '@cdab/scania/qpr/env-vars'
import { useFeatures } from '@cdab/scania/qpr/feature-flags'
import { capitalize } from '../utils'

import { useUser } from '@cdab/scania/qpr/contexts/user-provider'
import { useCssVariableBreakpoint } from '@cdab/scania/qpr/hooks'
import { Roles } from '@cdab/scania/qpr/schema'
import { capitalizeAllFirstLetters } from '@cdab/utils'
import {
  TdsIcon,
  TdsSideMenu,
  TdsSideMenuCloseButton,
  TdsSideMenuCollapseButton,
  TdsSideMenuDropdown,
  TdsSideMenuDropdownList,
  TdsSideMenuDropdownListItem,
  TdsSideMenuItem,
  TdsSideMenuOverlay
} from '@scania/tegel-react'
import { useLocation, useNavigate } from 'react-router-dom'

export function SideMenu({
  isMobileMenuOpen,
  closeMobileMenu
}: {
  isMobileMenuOpen: boolean
  closeMobileMenu: () => void
}) {
  const { t } = useTranslation(['favoriteAudits', 'common'])
  const { qpr2007url } = getEnvVarsQPR()
  const navigate = useNavigate()
  const location = useLocation()
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')
  const sideMenuRef = useRef<HTMLTdsSideMenuElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const features = useFeatures([
    'audit_shortcuts',
    'readonly_documents_auditor'
  ])

  const userContext = useUser()

  const isAdminHighest = userContext.userData.role === Roles.FactoryAdmin
  const isFactoyEngineAndHigher =
    userContext.userData.role >= Roles.FactoryEngineAdmin
  const isCordinatorAndHigher = userContext.userData.role >= Roles.CoOrdinator
  const isAuditorOnly = userContext.userData.role === Roles.Auditor

  // Helper function to determine if a menu item should be selected
  const isMenuItemSelected = (navigationPath: string): boolean => {
    const currentPath = location.pathname

    return (
      currentPath === navigationPath ||
      currentPath.startsWith(navigationPath + '/')
    )
  }

  useEffect(() => {
    if (sideMenuRef.current && wrapperRef.current) {
      const sideMenuElement = sideMenuRef.current
      if (isLg) {
        sideMenuElement.open = true
      } else {
        sideMenuElement.open = isMobileMenuOpen
      }
    }
  }, [isMobileMenuOpen, isLg])

  const handleNavigation = (path: string) => {
    navigate(path)
    closeMobileMenu()
  }

  const handleExternalNavigation = (url: string) => {
    window.open(url, '_blank')
    closeMobileMenu()
  }

  const handleCloseSideMenu = () => {
    if (sideMenuRef.current) {
      sideMenuRef.current.open = false
    }
    closeMobileMenu()
  }

  return (
    <div ref={wrapperRef} className='side-menu-wrapper' id='side-menu-wrapper'>
      <TdsSideMenu
        ref={sideMenuRef}
        id='app-side-menu'
        aria-label='Navigation menu'
        persistent={isLg}
        open={isLg ? true : isMobileMenuOpen}
        className='tds-w-100'
      >
        <TdsSideMenuOverlay slot='overlay' onClick={handleCloseSideMenu} />

        <TdsSideMenuCloseButton
          slot='close-button'
          onClick={handleCloseSideMenu}
        />

        <TdsSideMenuItem selected={isMenuItemSelected('/dashboard')}>
          <button onClick={() => handleNavigation('/dashboard')}>
            <TdsIcon name='home' size='24' />
            Dashboard
          </button>
        </TdsSideMenuItem>

        <TdsSideMenuItem selected={isMenuItemSelected('/my-audits')}>
          <button onClick={() => handleNavigation('/my-audits')}>
            <TdsIcon name='heart' size='24' />
            {capitalize(t('favorite-audits'))}
          </button>
        </TdsSideMenuItem>

        <TdsSideMenuItem selected={isMenuItemSelected('/dealers')}>
          <button onClick={() => handleNavigation('/dealers')}>
            <TdsIcon name='truck' size='24' />
            Dealers
          </button>
        </TdsSideMenuItem>

        <TdsSideMenuItem selected={isMenuItemSelected('/reports')}>
          <button onClick={() => handleNavigation('/reports')}>
            <TdsIcon name='document' size='24' />
            Reports
          </button>
        </TdsSideMenuItem>

        {features.readonly_documents_auditor.enabled && isAuditorOnly && (
          <TdsSideMenuItem selected={isMenuItemSelected('/documents')}>
            <button onClick={() => handleNavigation('/documents')}>
              <TdsIcon name='document' size='24' />
              Documents
            </button>
          </TdsSideMenuItem>
        )}

        <TdsSideMenuDropdown>
          <TdsIcon slot='icon' name='lock' size='24' />
          <span slot='label'>Admin</span>
          <TdsSideMenuDropdownList>
            {isCordinatorAndHigher && (
              <TdsSideMenuDropdownListItem
                selected={isMenuItemSelected('/admin/documents')}
              >
                <button onClick={() => handleNavigation('/admin/documents')}>
                  {capitalizeAllFirstLetters(
                    t('reference-documents', { ns: 'common' })
                  )}
                </button>
              </TdsSideMenuDropdownListItem>
            )}

            {isAdminHighest && (
              <TdsSideMenuDropdownListItem
                selected={isMenuItemSelected('/admin/system-messages')}
              >
                <button
                  onClick={() => handleNavigation('/admin/system-messages')}
                >
                  {t('system-messages', { ns: 'common' })}
                </button>
              </TdsSideMenuDropdownListItem>
            )}

            <TdsSideMenuDropdownListItem
              selected={isMenuItemSelected('/admin/translations')}
            >
              <button onClick={() => handleNavigation('/admin/translations')}>
                {t('translations', { ns: 'common' })}
              </button>
            </TdsSideMenuDropdownListItem>

            {isFactoyEngineAndHigher && (
              <TdsSideMenuDropdownListItem
                selected={isMenuItemSelected('/admin/users')}
              >
                <button onClick={() => handleNavigation('/admin/users')}>
                  {t('users', { ns: 'common' })}
                </button>
              </TdsSideMenuDropdownListItem>
            )}
          </TdsSideMenuDropdownList>
        </TdsSideMenuDropdown>

        {features.audit_shortcuts.enabled && (
          <>
            <TdsSideMenuItem selected={isMenuItemSelected('/audit/12345')}>
              <button onClick={() => handleNavigation('/audit/12345')}>
                <TdsIcon name='timer' size='24' />
                Sample Audit
              </button>
            </TdsSideMenuItem>

            <TdsSideMenuItem selected={isMenuItemSelected('/audit/14101')}>
              <button onClick={() => handleNavigation('/audit/14101')}>
                <TdsIcon name='star' size='24' />
                Good Audit
              </button>
            </TdsSideMenuItem>
          </>
        )}

        <TdsSideMenuCollapseButton slot='sticky-end' />
      </TdsSideMenu>
    </div>
  )
}
